import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, X } from 'lucide-react';
export function Footer() {
  const [activePage, setActivePage] = useState<'terms' | 'privacy' | null>(null);
  return <>
      <footer className="bg-slate-950 border-t border-slate-800 py-10 px-4 sm:px-6 lg:px-8" data-id="element-38">
        <div className="max-w-4xl mx-auto text-center" data-id="element-39">
          <p className="text-slate-500 text-sm mb-3" data-id="element-40">
            © {new Date().getFullYear()} The No-Knee Tax Ladder. All rights
            reserved.
          </p>
          <p className="text-slate-600 text-xs max-w-2xl mx-auto leading-relaxed mb-6" data-id="element-41">
            Disclaimer: The No-Knee Tax Ladder is a digital fitness guide for
            educational purposes only. It is not intended as medical advice,
            diagnosis, or treatment. Consult your physician before starting any
            exercise program. Individual results may vary. This product is sold
            and distributed by Better By Steps.
          </p>
          <div className="flex items-center justify-center gap-4 text-xs" data-id="element-42">
            <button onClick={() => setActivePage('terms')} className="text-slate-500 hover:text-brand-orange transition-colors uppercase tracking-widest underline underline-offset-4 decoration-slate-700 hover:decoration-brand-orange" data-id="element-43">
              Terms of Service
            </button>
            <span className="text-slate-700" data-id="element-44">|</span>
            <button onClick={() => setActivePage('privacy')} className="text-slate-500 hover:text-brand-orange transition-colors uppercase tracking-widest underline underline-offset-4 decoration-slate-700 hover:decoration-brand-orange" data-id="element-45">
              Privacy Policy
            </button>
          </div>
        </div>
      </footer>

      {/* Full-page overlay for Terms / Privacy */}
      <AnimatePresence data-id="element-46">
        {activePage && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25
      }} className="fixed inset-0 z-[100] bg-slate-950 overflow-y-auto" data-id="element-47">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-id="element-48">
              {/* Header */}
              <div className="flex items-center justify-between mb-10 border-b border-slate-800 pb-6" data-id="element-49">
                <button onClick={() => setActivePage(null)} className="flex items-center gap-2 text-slate-400 hover:text-brand-orange transition-colors text-sm uppercase tracking-widest" data-id="element-50">
                  <ArrowLeft className="w-4 h-4" data-id="element-51" />
                  Back
                </button>
                <button onClick={() => setActivePage(null)} className="text-slate-500 hover:text-white transition-colors" data-id="element-52">
                  <X className="w-5 h-5" data-id="element-53" />
                </button>
              </div>

              {activePage === 'terms' && <TermsContent data-id="element-54" />}
              {activePage === 'privacy' && <PrivacyContent data-id="element-55" />}

              {/* Bottom back link */}
              <div className="mt-12 pt-8 border-t border-slate-800 text-center" data-id="element-56">
                <button onClick={() => setActivePage(null)} className="text-slate-500 hover:text-brand-orange transition-colors text-xs uppercase tracking-widest underline underline-offset-4" data-id="element-57">
                  ← Back to main page
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
function TermsContent() {
  return <div className="text-slate-300" data-id="element-58">
      <h1 className="text-3xl md:text-4xl font-heading font-bold text-white uppercase mb-2" data-id="element-59">
        Terms of Service
      </h1>
      <p className="text-slate-500 text-sm mb-10" data-id="element-60">
        Last updated: February 2026
      </p>

      <div className="space-y-8 text-sm leading-relaxed" data-id="element-61">
        <section data-id="element-62">
          <h2 className="text-lg font-heading font-bold text-white uppercase mb-3" data-id="element-63">
            1. Acceptance of Terms
          </h2>
          <p className="text-slate-400" data-id="element-64">
            By accessing and using this website and purchasing any digital
            products from The No-Knee Tax Ladder, you accept and agree to be
            bound by the terms and provisions of this agreement. If you do not
            agree to these terms, please do not use this website or purchase our
            products.
          </p>
        </section>

        <section data-id="element-65">
          <h2 className="text-lg font-heading font-bold text-white uppercase mb-3" data-id="element-66">
            2. Digital Product
          </h2>
          <p className="text-slate-400" data-id="element-67">
            The No-Knee Tax Ladder is a digital product delivered
            electronically. No physical products will be shipped. Upon purchase,
            you will receive immediate access to download the program materials.
            All sales are for personal, non-commercial use only.
          </p>
        </section>

        <section data-id="element-68">
          <h2 className="text-lg font-heading font-bold text-white uppercase mb-3" data-id="element-69">
            3. Medical Disclaimer
          </h2>
          <p className="text-slate-400" data-id="element-70">
            The content provided in this program is for informational and
            educational purposes only and is not intended as medical advice,
            diagnosis, or treatment. Always seek the advice of your physician or
            other qualified health provider before beginning any exercise
            program. Never disregard professional medical advice or delay in
            seeking it because of something you have read in this program. If
            you experience any pain or difficulty with exercises, stop
            immediately and consult your healthcare provider.
          </p>
        </section>

        <section data-id="element-71">
          <h2 className="text-lg font-heading font-bold text-white uppercase mb-3" data-id="element-72">
            4. Refund Policy
          </h2>
          <p className="text-slate-400" data-id="element-73">
            We offer a 30-day money-back guarantee. If you are not satisfied
            with your purchase for any reason, contact us at
            support@betterbysteps.com within 30 days of your purchase date for a
            full refund. No questions asked.
          </p>
        </section>

        <section data-id="element-74">
          <h2 className="text-lg font-heading font-bold text-white uppercase mb-3" data-id="element-75">
            5. Intellectual Property
          </h2>
          <p className="text-slate-400" data-id="element-76">
            All content included in this program, including text, graphics,
            logos, images, videos, and software, is the property of The No-Knee
            Tax Ladder and is protected by international copyright laws. You may
            not reproduce, distribute, modify, create derivative works of,
            publicly display, or in any way exploit any of the content without
            prior written permission.
          </p>
        </section>

        <section data-id="element-77">
          <h2 className="text-lg font-heading font-bold text-white uppercase mb-3" data-id="element-78">
            6. Limitation of Liability
          </h2>
          <p className="text-slate-400" data-id="element-79">
            In no event shall The No-Knee Tax Ladder, its owners, employees, or
            affiliates be liable for any direct, indirect, incidental, special,
            consequential, or exemplary damages resulting from the use or
            inability to use the program, or for the cost of procurement of
            substitute goods and services. Your use of this program is at your
            sole risk.
          </p>
        </section>

        <section data-id="element-80">
          <h2 className="text-lg font-heading font-bold text-white uppercase mb-3" data-id="element-81">
            7. Contact
          </h2>
          <p className="text-slate-400" data-id="element-82">
            For any questions regarding these terms, please contact us at{' '}
            <span className="text-brand-orange" data-id="element-83">support@betterbysteps.com</span>
            .
          </p>
        </section>
      </div>
    </div>;
}
function PrivacyContent() {
  return <div className="text-slate-300" data-id="element-84">
      <h1 className="text-3xl md:text-4xl font-heading font-bold text-white uppercase mb-2" data-id="element-85">
        Privacy Policy
      </h1>
      <p className="text-slate-500 text-sm mb-10" data-id="element-86">
        Last updated: February 2026
      </p>

      <div className="space-y-8 text-sm leading-relaxed" data-id="element-87">
        <section data-id="element-88">
          <h2 className="text-lg font-heading font-bold text-white uppercase mb-3" data-id="element-89">
            1. Information We Collect
          </h2>
          <p className="text-slate-400" data-id="element-90">
            When you purchase our product or interact with our website, we may
            collect the following information: your name, email address, billing
            address, and payment information. Payment processing is handled
            securely by our third-party payment processor — we do not store your
            full credit card details on our servers.
          </p>
        </section>

        <section data-id="element-91">
          <h2 className="text-lg font-heading font-bold text-white uppercase mb-3" data-id="element-92">
            2. How We Use Your Information
          </h2>
          <p className="text-slate-400" data-id="element-93">
            We use the information we collect to: process your order and deliver
            your digital product, send you order confirmations and important
            updates about your purchase, provide customer support, and send
            occasional emails about product updates or related offerings (you
            can unsubscribe at any time).
          </p>
        </section>

        <section data-id="element-94">
          <h2 className="text-lg font-heading font-bold text-white uppercase mb-3" data-id="element-95">
            3. Data Protection
          </h2>
          <p className="text-slate-400" data-id="element-96">
            We implement industry-standard security measures to protect your
            personal information, including SSL encryption for all data
            transmission and secure storage practices. We regularly review our
            security procedures to ensure your data remains protected.
          </p>
        </section>

        <section data-id="element-97">
          <h2 className="text-lg font-heading font-bold text-white uppercase mb-3" data-id="element-98">
            4. Third-Party Disclosure
          </h2>
          <p className="text-slate-400" data-id="element-99">
            We do not sell, trade, or otherwise transfer your personally
            identifiable information to outside parties. This does not include
            trusted third parties who assist us in operating our website,
            conducting our business, or servicing you, so long as those parties
            agree to keep this information confidential.
          </p>
        </section>

        <section data-id="element-100">
          <h2 className="text-lg font-heading font-bold text-white uppercase mb-3" data-id="element-101">
            5. Cookies
          </h2>
          <p className="text-slate-400" data-id="element-102">
            We use cookies to understand and save your preferences for future
            visits, keep track of advertisements, and compile aggregate data
            about site traffic and site interaction so that we can offer better
            site experiences in the future.
          </p>
        </section>

        <section data-id="element-103">
          <h2 className="text-lg font-heading font-bold text-white uppercase mb-3" data-id="element-104">
            6. Your Rights
          </h2>
          <p className="text-slate-400" data-id="element-105">
            You have the right to request access to the personal data we hold
            about you, request correction of any inaccurate data, request
            deletion of your personal data, and opt out of marketing
            communications at any time. To exercise any of these rights, please
            contact us.
          </p>
        </section>

        <section data-id="element-106">
          <h2 className="text-lg font-heading font-bold text-white uppercase mb-3" data-id="element-107">
            7. Contact
          </h2>
          <p className="text-slate-400" data-id="element-108">
            For any questions regarding this privacy policy or your personal
            data, please contact us at{' '}
            <span className="text-brand-orange" data-id="element-109">support@betterbysteps.com</span>
            .
          </p>
        </section>
      </div>
    </div>;
}